/** Actions */
import axios from 'axios';

export { store } from './store';
export { authActions, authService } from './auth';
export { permissionsActions } from './permissions';
export { roleActions } from './role';
export { adminActions } from './admin';
export { fundingSourceActions } from './fundingSource';
export { clientActions } from './client';
export { systemActions } from './system';
export { payrollActions } from './payroll';
export { noteActions } from './notes';
export { commentActions, commentThreadKey } from './comments';
export { aiNoteActions } from './aiNote';
export { aiTransformActions } from './aiTransform';
export { availabilityScheduleActions } from './availabilitySchedule';
export { appointmentActions } from './appointment';
export { claimActions, invoiceActions, invoiceTxnActions } from './billing';
export { claimPaymentActions, invoicePaymentActions } from './posting';
export { historyActions } from './history';
export { reportActions } from './reports';
export { staffPayrollActions } from './staffPayroll';
export { notificationActions } from './notification';
export { analyticActions } from './analytics';
export { dashboardActions } from './dashboard';
export { tagActions } from './tags';
export { mapActions } from './map';
export { formActions } from './form';
export { resourceActions } from './resource';
export { fileActions } from './file';
export { httpRequestsOnSuccessActions } from './http_requests_on_success';
export { httpRequestsOnErrorsActions } from './http_requests_on_errors';
export { httpRequestsOnLoadActions } from './http_requests_on_load';

export { taskActions } from './tasks';

export { userPreferencesActions } from './userPreferences';

export { signatureRequestActions } from './signatureRequest';

/** Clinical */

export {
    goalActions,
    domainActions,
    assessmentActions,
    abcActions,
    sessionActions,
    eventActions,
    baselineActions,
    progressReportsActions,
} from './clinical';
export { libraryActions } from './library';
export { chatActions } from './chat';
export { supervisionActions } from './supervision';
export { postedNoteActions } from './postedNote';
export { versionsActions } from './versions';
export { tutorialVideosActions } from './tutorialVideos';
export { clientDiagnosisActions } from './clientDiagnosis';
export { wishlistActions } from './wishlist';
export { documentVersionActions } from './documentVersion';

/** End */

export const UploadServices = {
    DeleteImage: (file) => axios.delete('/files', { auth: true, data: file }),

    DeleteImageByKey: (keys) => axios.delete('/files/bulk', { auth: true, params: { keys: keys } }),

    // Video metadata endpoints. The actual video bytes upload directly
    // from browser to S3 via the multipart uploadVideo() helper below;
    // these endpoints just persist the metadata (POST /videos/complete),
    // mint signed playback URLs (GET /videos/generate-url), and delete
    // (DELETE + POST /videos/multi-abort).
    //
    // Legacy chunk-through-api endpoints (VideoStart, UploadChunk,
    // VideoPart, VideoAbort) are removed — chunks no longer flow through
    // the API, so there's nothing to start/abort server-side. The SDK's
    // uploadVideo() aborts the S3 multipart client-side on error; S3
    // lifecycle policy cleans dangling parts after 24h as a backstop.
    VideoComplete: (body) => axios.post('/videos/complete', body, { auth: true, params: '' }),

    DeleteVideo: (resourceId, documentId, model) => axios.delete(`/videos/${resourceId}/${documentId}/${model}`, { auth: true, params: '' }),

    VideoAbortMultiple: (body) => axios.post(`/videos/multi-abort`, body, { auth: true }),

    VideoUrl: (key) => axios.get('/videos/generate-url', { auth: true, params: { key: key } }),
};

// ─── File upload SDK — final design ─────────────────────────────────────
//
// All file uploads in the entire app (avatar, signature, clinical doc,
// video, claim/invoice attachments, intake forms, company logo) go
// through these helpers. Bytes never proxy through api — clients PUT
// directly to S3 via presigned URLs.
//
// Returns: StoredFile = { key, originalName, format, contentType,
//   sizeBytes, uploader?, uploadedAt } — the canonical embedded shape.
// Splice the result directly onto the parent doc (e.g.,
// staff.avatar = await uploadFile(file, KeyPurpose.AVATAR)) and PATCH.

const FILES_API = {
    presign: (body) => axios.post('/files/presign', body, { auth: true }),
    multipartStart: (body) => axios.post('/files/multipart/start', body, { auth: true }),
    multipartSignPart: (body) => axios.post('/files/multipart/sign-part', body, { auth: true }),
    multipartComplete: (body) => axios.post('/files/multipart/complete', body, { auth: true }),
    multipartAbort: (body) => axios.post('/files/multipart/abort', body, { auth: true }),
    deleteFile: (body) => axios.delete('/files', { auth: true, data: body }),
    presignInvite: (body, inviteToken) => axios.post('/files/invite/presign', body, { headers: { 'invite-token': inviteToken } }),
};

/**
 * Single-shot file upload for sizes ≤ 100 MB. The most common case.
 * `purpose` MUST be a value from `KeyPurpose` (utils/constants.js).
 */
export async function uploadFile(file, purpose) {
    const presignRes = await FILES_API.presign({
        purpose,
        originalName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
    });
    const { uploadUrl, requiredHeaders, file: storedFile } = presignRes.data;

    // Direct PUT to S3 — bytes never proxy through api. Bare axios to
    // skip the global access-token interceptor on the cross-origin PUT.
    const bareAxios = axios.create();
    await bareAxios.put(uploadUrl, file, { headers: requiredHeaders });

    return storedFile;
}

/**
 * Multipart upload for large files (clinical videos > 100 MB, up to 5 GB).
 * Splits into 10 MB parts, uploads each via presigned URL, then
 * completes with the collected ETags.
 *
 * opts.onProgress({ partNumber, totalParts, bytesUploaded, bytesTotal, percent })
 * fires as bytes leave the browser (not just per part, so a file under one
 * part still moves the bar) — wire up to a progress bar.
 */
const MULTIPART_PART_SIZE = 10 * 1024 * 1024; // 10 MB

export async function uploadVideo(file, purpose, opts = {}) {
    const startRes = await FILES_API.multipartStart({
        purpose,
        originalName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
    });
    const { key, uploadId, file: storedFile } = startRes.data;

    const totalParts = Math.ceil(file.size / MULTIPART_PART_SIZE);
    const completedParts = [];
    const bareAxios = axios.create();
    const reportProgress = (partNumber, bytesUploaded) => {
        if (typeof opts.onProgress !== 'function') return;
        opts.onProgress({
            partNumber,
            totalParts,
            bytesUploaded,
            bytesTotal: file.size,
            percent: file.size ? Math.min(100, Math.round((bytesUploaded / file.size) * 100)) : 100,
        });
    };

    try {
        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
            const start = (partNumber - 1) * MULTIPART_PART_SIZE;
            const end = Math.min(start + MULTIPART_PART_SIZE, file.size);
            const blob = file.slice(start, end);

            const signRes = await FILES_API.multipartSignPart({ key, uploadId, partNumber });
            const putRes = await bareAxios.put(signRes.data.uploadUrl, blob, {
                onUploadProgress: ({ loaded }) => reportProgress(partNumber, start + Math.min(loaded, end - start)),
            });
            const etag = putRes.headers.etag || putRes.headers.ETag;
            if (!etag) throw new Error(`Part ${partNumber} PUT did not return ETag`);

            completedParts.push({ partNumber, etag: etag.replace(/^"|"$/g, '') });
            reportProgress(partNumber, end);
        }

        await FILES_API.multipartComplete({ key, uploadId, parts: completedParts });
        return storedFile;
    } catch (err) {
        // Best-effort abort. If it fails too, server-side lifecycle
        // policy cleans up dangling multipart uploads after 24h.
        try {
            await FILES_API.multipartAbort({ key, uploadId });
        } catch {
            // swallow
        }
        throw err;
    }
}

/**
 * Invite-token-authed upload (intake form attachments where the user
 * isn't logged in but holds a valid invite token).
 */
export async function uploadFileWithInvite(file, purpose, inviteToken, orgIdHint) {
    const presignRes = await FILES_API.presignInvite(
        {
            purpose,
            originalName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            orgIdHint,
        },
        inviteToken,
    );
    const { uploadUrl, requiredHeaders, file: storedFile } = presignRes.data;

    const bareAxios = axios.create();
    await bareAxios.put(uploadUrl, file, { headers: requiredHeaders });

    return storedFile;
}

/**
 * Upload a file with an immediate local blob-URL preview.
 *
 * Returns `{previewUrl, storedFile}`:
 *   - `previewUrl`: a `URL.createObjectURL(file)` string usable as
 *     `<img src>` RIGHT AWAY, before the S3 PUT completes. Wire it
 *     into your field state (as `.url` on the StoredFile shape, or a
 *     sibling `previewUrl` field, whichever your render code prefers)
 *     so the user sees the image without waiting on the network.
 *   - `storedFile`: `Promise<StoredFile>` resolving when the S3 PUT
 *     succeeds. Await it, then merge the canonical fields (key,
 *     contentType, sizeBytes, etc.) into your field state.
 *
 * On upload failure `storedFile` rejects — callers should reset their
 * field to `null` so the user retries clean instead of being stuck
 * with a blob-URL-only ghost record that can't save.
 *
 * Blob URL lifecycle: the URL stays valid for the life of the browser
 * tab. Keep it on `.url` for the rest of the edit session. On save,
 * mongoose strict mode drops it (StoredFileSchema has no `.url`); on
 * reload the read-path sanitizer stamps a fresh signed URL. One
 * field, three lives: blob during edit → dropped on write → signed
 * URL on read.
 *
 * `opts.inviteToken` (+ optional `opts.orgIdHint`) routes to the
 * invite-authed presign for intake-form uploads.
 */
export function uploadFileWithPreview(file, purpose, opts = {}) {
    const previewUrl = URL.createObjectURL(file);
    const storedFile = opts.inviteToken
        ? uploadFileWithInvite(file, purpose, opts.inviteToken, opts.orgIdHint)
        : uploadFile(file, purpose);
    return { previewUrl, storedFile };
}

/** Delete an S3 object by its tenant-scoped key. Idempotent. */
export async function deleteFile(key) {
    await FILES_API.deleteFile({ key });
}

export const CsvService = {
    getStaffCsv: () => axios.get('/reports/staff', { auth: true }),

    getClientCsv: () => axios.get('/reports/client', { auth: true }),

    getScheduleCsv: (params) => axios.get('/reports/appt', { auth: true, params: params }),

    getFundingSourceCsv: () => axios.get('/reports/fs', { auth: true }),

    getClaimPaymentCsv: (params) => axios.get('/reports/posting/claim', { auth: true, params: params }),

    getInvoicePaymentCsv: (params) => axios.get('/reports/posting/invoice', { auth: true, params: params }),

    activePassivePayrollCsv: (params) => axios.get('/reports/payroll', { auth: true, params: { ...params } }),

    pendingClaimCsv: (params) => axios.get('/reports/billing/claim', { auth: true, params: { ...params } }),

    apptSessionCsv: (params) => axios.get(`/reports/clinical/session/${params?.sessionId}/dataCollection`, { auth: true }),

    apptSessionNoteCsv: (params) =>
        axios.get(`/reports/appt/${params?.apptId}/notes/${params?.noteId}`, { auth: true, responseType: 'blob' }),

    sessionDataCsv: (params) => axios.get(`/reports/clinical/goal/session`, { auth: true, params: { ...params } }),

    billingInvoiceCsv: (params) => axios.get('/reports/billing/invoice', { auth: true, params: { ...params } }),

    getClientNoteCsv: (clientId, params) => axios.get(`/reports/client/${clientId}/notes`, { auth: true, params: { ...params } }),
};

export const AddressService = {
    getAddress: (address) => axios.post('/address', { address: address }, { auth: true }),
};
